import * as React from "react";
import { DatePicker } from "@repo/ui";
import { formatToApiDate, parseDateString } from "@repo/utils";

export interface DateInputProps {
  value?: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  name?: string;
  "data-testid"?: string;
}

/** Safely turn a yyyy-mm-dd string into a Date, or undefined. */
function toDate(value?: string): Date | undefined {
  if (!value) return undefined;
  try {
    return parseDateString(value);
  } catch {
    return undefined;
  }
}

/**
 * Adapter around the shared DatePicker for forms that store dates as
 * yyyy-mm-dd strings (filters, manual-state forms, API fields).
 */
export function DateInput({
  value,
  onChange,
  min,
  max,
  placeholder,
  disabled,
  className,
  id,
  name,
  "data-testid": dataTestId,
}: DateInputProps) {
  const dateValue = React.useMemo(() => toDate(value), [value]);
  const minValue = React.useMemo(() => toDate(min), [min]);
  const maxValue = React.useMemo(() => toDate(max), [max]);

  return (
    <DatePicker
      date={dateValue}
      min={minValue}
      max={maxValue}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
      id={id}
      name={name}
      data-testid={dataTestId}
      onDateChange={(date) => onChange(date ? formatToApiDate(date) : "")}
    />
  );
}
